import { useState } from "react";
import { Link } from "react-router-dom";
import { useSiteData } from "@/context/SiteDataContext";
import { useT } from "@/lib/useSiteText";
import BodegaModal from "@/pages/bodegas/BodegaModal";
import type { Bodega } from "@/types/content";

export default function FindSpace() {
  const { data, whatsappLink } = useSiteData();
  const t = useT();
  const etiqueta = t("home.encuentra.etiqueta");
  const descripcion = t("home.encuentra.descripcion");
  const destacadas = data.bodegas.filter((b) => b.destacada);
  const bodegas = (destacadas.length > 0 ? destacadas : data.bodegas).slice(0, 4);
  const [openBodega, setOpenBodega] = useState<Bodega | null>(null);

  return (
    <section className="bg-background-100">
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-16 md:py-24">
        <div className="max-w-2xl">
          {etiqueta && <span className="text-sm font-semibold text-accent-600">{etiqueta}</span>}
          <h2 className="mt-2 font-heading font-bold text-2xl md:text-4xl text-foreground-950">
            {t("home.encuentra.titulo")}
          </h2>
          {descripcion && <p className="mt-4 text-base md:text-lg text-foreground-600">{descripcion}</p>}
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {bodegas.map((b) => (
            <div
              key={b.id}
              className="group rounded-lg bg-background-50 border border-background-200 overflow-hidden hover:-translate-y-1 transition-transform duration-300 flex flex-col"
            >
              <button
                type="button"
                onClick={() => setOpenBodega(b)}
                className="relative h-44 overflow-hidden block w-full cursor-pointer"
              >
                <img
                  src={b.imagen_url}
                  alt={b.imagen_alt}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span
                  className={`absolute top-3 left-3 rounded-full px-3 py-1 text-xs font-semibold ${
                    b.disponible ? "bg-primary-500 text-background-50" : "bg-background-300 text-foreground-700"
                  }`}
                >
                  {b.disponible ? t("bodegas.badge_disponible") : t("bodegas.badge_ocupado")}
                </span>
              </button>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-heading font-semibold text-lg text-foreground-900">{b.nombre}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                  {b.m2} m² — {t("bodegas.precio_desde").toLocaleLowerCase("es")}{" "}
                  <strong className="text-foreground-800 font-semibold">UF {b.valor_uf.toLocaleString("es-CL")}</strong>{" "}
                  {t("bodegas.precio_sufijo")}
                </p>
                <div className="mt-4 mb-5 flex flex-wrap gap-2">
                  {b.caracteristicas.slice(0, 2).map((c) => (
                    <span
                      key={c}
                      className="text-xs rounded-full bg-secondary-100 text-secondary-900 px-2.5 py-1 whitespace-nowrap"
                    >
                      {c}
                    </span>
                  ))}
                </div>
                <div className="mt-auto pt-4 border-t border-background-200">
                  <button
                    type="button"
                    onClick={() => setOpenBodega(b)}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 transition cursor-pointer"
                  >
                    {t("home.encuentra.ver_detalle")} <i className="ri-arrow-right-line" />
                  </button>
                  <a
                    href={whatsappLink(t("wa.cotizar_bodega", { nombre: b.nombre }))}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-3 inline-flex items-center gap-1.5 text-sm font-semibold text-accent-600 hover:text-accent-700 transition"
                  >
                    {t("home.encuentra.cotizar")}
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/bodegas"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 transition"
          >
            {t("home.encuentra.ver_catalogo")} <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>

      <BodegaModal bodega={openBodega} onClose={() => setOpenBodega(null)} whatsappLink={whatsappLink} />
    </section>
  );
}